import { __decorate } from "tslib";
import { AnyDecoder, ArrayDecoder, AutoEncoder, BooleanDecoder, EnumDecoder, field, MapDecoder, StringDecoder } from '@simonbackx/simple-encoding';
import { Formatter } from '@stamhoofd/utility';
import { v4 as uuidv4 } from "uuid";
export var PermissionLevel;
(function (PermissionLevel) {
    /** No access */
    PermissionLevel["None"] = "None";
    /** Read all data, but not allowed to write */
    PermissionLevel["Read"] = "Read";
    /** Read, write, add, delete child data, but not allowed to modify settings */
    PermissionLevel["Write"] = "Write";
    /** Full access */
    PermissionLevel["Full"] = "Full";
})(PermissionLevel || (PermissionLevel = {}));
export function getPermissionLevelNumber(level) {
    switch (level) {
        case PermissionLevel.None: return 0;
        case PermissionLevel.Read: return 1;
        case PermissionLevel.Write: return 2;
        case PermissionLevel.Full: return 3;
        default: {
            const l = level; // will throw compile error if new levels are added without editing this method
            throw new Error("Unknown permission level " + l);
        }
    }
}
export class PermissionRole extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.name = "";
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], PermissionRole.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder })
], PermissionRole.prototype, "name", void 0);
export class PermissionRoleDetailed extends PermissionRole {
    constructor() {
        super(...arguments);
        /**
         * Generic access to all resources
         */
        this.level = PermissionLevel.None;
        // Todo: all these flag based permissions should move to a flag list
        // so it is easier to add more permission levels in the future.
        // also, permission flags should be prefixed with either organization or platform (e.g. managing invoices of the platform vs managing finances of all organizations)
        /**
         * Access to open transfers
         */
        this.managePayments = false;
        /**
         * Full payments access
         */
        this.financeDirector = false;
        /**
         * Can create new webshops = write
         */
        this.createWebshops = false;
    }
    getDescription(webshops, groups) {
        var _a;
        const stack = [];
        if (this.level === PermissionLevel.Read) {
            stack.push("alles lezen");
        }
        if (this.level === PermissionLevel.Write) {
            stack.push("alles bewerken");
        }
        if (this.level === PermissionLevel.Full) {
            stack.push("alles");
        }
        if (this.financeDirector) {
            stack.push("volledige boekhouding");
        }
        else if (this.managePayments) {
            stack.push("overschrijvingen");
        }
        if (this.createWebshops) {
            stack.push("webshops maken");
        }
        let webshopCount = 0;
        for (const webshop of webshops) {
            if (webshop.privateMeta.permissions.roleHasAccess(this)) {
                webshopCount++;
                continue;
            }
            if (webshop.privateMeta.scanPermissions.roleHasAccess(this)) {
                webshopCount++;
                continue;
            }
        }
        if (webshopCount > 0) {
            stack.push(webshopCount + " webshop" + (webshopCount > 1 ? "s" : ""));
        }
        let groupCount = 0;
        for (const group of groups) {
            if ((_a = group.privateSettings) === null || _a === void 0 ? void 0 : _a.permissions.roleHasAccess(this)) {
                groupCount++;
                continue;
            }
        }
        if (groupCount > 0) {
            stack.push(groupCount + " groep" + (groupCount > 1 ? "en" : ""));
        }
        if (stack.length === 0) {
            return "geen toegang";
        }
        return Formatter.capitalizeFirstLetter(Formatter.joinLast(stack, ', ', ' en '));
    }
}
__decorate([
    field({ decoder: new EnumDecoder(PermissionLevel), version: 201 })
], PermissionRoleDetailed.prototype, "level", void 0);
__decorate([
    field({ decoder: BooleanDecoder })
], PermissionRoleDetailed.prototype, "managePayments", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 199 })
], PermissionRoleDetailed.prototype, "financeDirector", void 0);
__decorate([
    field({ decoder: BooleanDecoder })
], PermissionRoleDetailed.prototype, "createWebshops", void 0);
/**
 * Give access to a given resouce based by the roles of a user
 */
export class PermissionsByRole extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.read = [];
        this.write = [];
        this.full = [];
    }
    /**
     * Whetever a given user has access to the members in this group.
     */
    getPermissionLevel(permissions, allRoles) {
        if (permissions.hasFullAccess(allRoles)) {
            return PermissionLevel.Full;
        }
        for (const role of this.full) {
            if (permissions.roles.find(r => r.id === role.id)) {
                return PermissionLevel.Full;
            }
        }
        if (permissions.hasWriteAccess(allRoles)) {
            return PermissionLevel.Write;
        }
        for (const role of this.write) {
            if (permissions.roles.find(r => r.id === role.id)) {
                return PermissionLevel.Write;
            }
        }
        if (permissions.hasReadAccess(allRoles)) {
            return PermissionLevel.Read;
        }
        for (const role of this.read) {
            if (permissions.roles.find(r => r.id === role.id)) {
                return PermissionLevel.Read;
            }
        }
        return PermissionLevel.None;
    }
    /**
    * Whetever a given user has access to the members in this group.
    */
    getRolePermissionLevel(role) {
        for (const r of this.full) {
            if (r.id === role.id) {
                return PermissionLevel.Full;
            }
        }
        for (const r of this.write) {
            if (r.id === role.id) {
                return PermissionLevel.Write;
            }
        }
        for (const r of this.read) {
            if (r.id === role.id) {
                return PermissionLevel.Read;
            }
        }
        return PermissionLevel.None;
    }
    hasAccess(permissions, allRoles, level) {
        if (!permissions) {
            return false;
        }
        return getPermissionLevelNumber(this.getPermissionLevel(permissions, allRoles)) >= getPermissionLevelNumber(level);
    }
    roleHasAccess(role, level = PermissionLevel.Read) {
        return getPermissionLevelNumber(this.getRolePermissionLevel(role)) >= getPermissionLevelNumber(level);
    }
    hasFullAccess(permissions, allRoles) {
        return this.hasAccess(permissions, allRoles, PermissionLevel.Full);
    }
    hasWriteAccess(permissions, allRoles) {
        return this.hasAccess(permissions, allRoles, PermissionLevel.Write);
    }
    hasReadAccess(permissions, allRoles) {
        return this.hasAccess(permissions, allRoles, PermissionLevel.Read);
    }
}
__decorate([
    field({ decoder: new ArrayDecoder(PermissionRole) })
], PermissionsByRole.prototype, "read", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(PermissionRole) })
], PermissionsByRole.prototype, "write", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(PermissionRole) })
], PermissionsByRole.prototype, "full", void 0);
export class Permissions extends AutoEncoder {
    constructor() {
        super(...arguments);
        /**
         * Automatically have all permissions (e.g. when someone created a new group)
         * Also allows creating new groups
         */
        this.level = PermissionLevel.None;
        /**
         * @deprecated
         */
        this.groups = [];
        this.roles = [];
    }
    hasAccess(allRoles, level) {
        if (getPermissionLevelNumber(this.level) >= getPermissionLevelNumber(level)) {
            // Someone with read / write access for the whole organization, also the same access for each group
            return true;
        }
        for (const r of this.roles) {
            const f = allRoles.find(rr => r.id === rr.id);
            if (!f) {
                // Deleted role
                continue;
            }
            if (getPermissionLevelNumber(f.level) >= getPermissionLevelNumber(level)) {
                return true;
            }
        }
        return false;
    }
    hasReadAccess(allRoles) {
        return this.hasAccess(allRoles, PermissionLevel.Read);
    }
    hasWriteAccess(allRoles) {
        return this.hasAccess(allRoles, PermissionLevel.Write);
    }
    hasFullAccess(allRoles) {
        return this.hasAccess(allRoles, PermissionLevel.Full);
    }
    /**
     * @param roles All available roles of the organizatino (to query)
     */
    canCreateWebshops(allRoles) {
        if (this.hasFullAccess(allRoles)) {
            return true;
        }
        for (const r of this.roles) {
            const f = allRoles.find(rr => r.id === rr.id);
            if (!f) {
                // Deleted role
                continue;
            }
            if (f.createWebshops) {
                return true;
            }
        }
        return false;
    }
    /**
     * @param roles All available roles of the organizatino (to query)
     */
    canManagePayments(allRoles) {
        if (this.hasFullAccess(allRoles)) {
            return true;
        }
        for (const r of this.roles) {
            const f = allRoles.find(rr => r.id === rr.id);
            if (!f) {
                // Deleted role
                continue;
            }
            if (f.financeDirector) {
                return true;
            }
            if (f.managePayments) {
                return true;
            }
        }
        return false;
    }
    /**
     */
    hasFinanceAccess(allRoles) {
        if (this.hasFullAccess(allRoles)) {
            return true;
        }
        for (const r of this.roles) {
            const f = allRoles.find(rr => r.id === rr.id);
            if (!f) {
                // Deleted role
                continue;
            }
            if (f.financeDirector) {
                return true;
            }
        }
        return false;
    }
    add(other) {
        if (getPermissionLevelNumber(this.level) < getPermissionLevelNumber(other.level)) {
            this.level = other.level;
        }
        for (const role of other.roles) {
            if (!this.roles.find(r => r.id === role.id)) {
                this.roles.push(role.clone());
            }
        }
    }
}
__decorate([
    field({ decoder: new EnumDecoder(PermissionLevel) })
], Permissions.prototype, "level", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(AnyDecoder), optional: true })
], Permissions.prototype, "groups", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(PermissionRole), version: 60 })
], Permissions.prototype, "roles", void 0);
export class UserPermissions extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.globalPermissions = Permissions.create({});
        this.organizationPermissions = new Map();
    }
}
__decorate([
    field({ decoder: Permissions })
], UserPermissions.prototype, "globalPermissions", void 0);
__decorate([
    field({ decoder: new MapDecoder(StringDecoder, Permissions) })
], UserPermissions.prototype, "organizationPermissions", void 0);
/**
 * Convenience class to combine both roles of an organization, platform and the permissions of a user.
 * This helps with checking permissions.
 */
export class FilledUserPermissions {
    constructor(userPermissions, allRoles) {
        // todo
    }
}
//# sourceMappingURL=Permissions.js.map